import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Subject, firstValueFrom } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ProductsService } from '../../../../services/products.service';
import { StoreDTO } from '../../../../core/interfaces/stores.interface';
import { CenterLookupDto } from 'src/app/shared/interfaces/center.interface';
import { UserRoleService } from 'src/app/core/services/role.service';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { StockService } from '../../services/stock.service';
import {
  BulkUploadJobResponse,
  BulkUploadProduct,
  BulkUploadProductPayloadDto,
  BulkUploadResponse,
} from '../../interfaces/stock.interface';
import { ConfirmMessageComponent } from 'src/app/shared/confirm-message-modal/confirm-message.component';
import { BulkUploadResultsModalComponent } from '../bulk-upload-results-modal/bulk-upload-results-modal.component';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { ProductCategory } from 'src/app/core/interfaces/product.interface';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { ImportSchemaService } from '../../services/import-schema.service';
import { CsvParseError, CsvParsingService, ParsedCsv } from '../../services/csv-parsing.service';
import {
  PayloadBuilderService,
  RowConversionError,
} from '../../services/payload-builder.service';
import { BulkUploadJobTrackerService } from '../../services/bulk-upload-job-tracker.service';
import { ImportSchema } from '../../interfaces/import-schema.interface';
import {
  ColumnMappingModalComponent,
  ColumnMappingModalData,
  ColumnMappingResult,
} from '../column-mapping-modal/column-mapping-modal.component';
import {
  DestructiveConfirmModalComponent,
  DestructiveConfirmModalData,
} from '../destructive-confirm-modal/destructive-confirm-modal.component';

interface BulkUploadModalData {
  storeId?: number;
  storeName?: string;
}

export interface ImportRowsResult {
  amountOfFiles: number;
  validRows: number;
  invalidRows: number;
}

@Component({
  selector: 'app-bulk-upload-modal',
  templateUrl: './bulk-upload-modal.component.html',
  styleUrls: ['./bulk-upload-modal.component.scss']
})
export class BulkUploadModalComponent implements OnInit, OnDestroy {
  private readonly maxFileSizeMb = 10;
  private readonly maxFileSizeBytes = this.maxFileSizeMb * 1024 * 1024;

  currentStep: number = 1;
  uploadForm: FormGroup;
  productCategories: ProductCategory[] = [];
  stores: StoreDTO[] = [];
  centers: CenterLookupDto[] = [];
  isGroupAdmin: boolean = false;
  isLoadingStores: boolean = false;
  selectedFile: File | null = null;
  parsedProducts: BulkUploadProduct[] = [];
  payloadProducts: ReadonlyArray<BulkUploadProductPayloadDto> = [];
  isMappingReady: boolean = false;
  fileError: string = '';
  isProcessing: boolean = false;
  isPreparingResults: boolean = false;
  isClosingDelay: boolean = false;
  private closeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  previewResponse: BulkUploadResponse | null = null;
  isPredefinedStore: boolean = false;
  predefinedStoreName: string = '';
  isPredefinedCategory: boolean = true;
  predefinedCategoryName: string = 'Neumáticos';
  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<BulkUploadModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BulkUploadModalData,
    private readonly storeService: ProductsService,
    private readonly productCategoryService: ProductCategoryService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService,
    private readonly stockService: StockService,
    private readonly dialog: MatDialog,
    private readonly analytics: AnalyticsService,
    private readonly importSchema: ImportSchemaService,
    private readonly csvParsing: CsvParsingService,
    private readonly payloadBuilder: PayloadBuilderService,
    private readonly userRoleService: UserRoleService,
    private readonly jobTracker: BulkUploadJobTrackerService,
  ) {
    this.uploadForm = this.fb.group({
      productCategoryId: ['', Validators.required],
      centerId: [null],
      storeId: ['', Validators.required],
      replaceStock: [false],
    });
  }

  ngOnInit(): void {
    this.loadCategories();
    this.checkPredefinedStore();
    this.setupPredefinedCategory();
    this.resolveRoleAndBootstrap();
  }

  ngOnDestroy(): void {
    if (this.closeTimeoutId) {
      clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveRoleAndBootstrap(): void {
    this.userRoleService.getCurrentUser()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          const highestRole = this.userRoleService.getHighestRole(user);
          this.isGroupAdmin = highestRole === 'groupadmin';

          if (this.isPredefinedStore) {
            this.loadStores(null);
            return;
          }

          if (this.isGroupAdmin) {
            this.uploadForm.get('centerId')?.setValidators(Validators.required);
            this.uploadForm.get('centerId')?.updateValueAndValidity();
            this.uploadForm.get('storeId')?.disable();
            this.loadCenters();
            this.setupCenterChangeHandler();
          } else {
            this.loadStores(null);
          }
        },
        error: () => {
          this.isGroupAdmin = false;
          this.loadStores(null);
        },
      });
  }

  private loadCenters(): void {
    this.stockService.getCentersLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (centers) => {
          this.centers = centers;
        },
        error: () => {
          this.snackbarService.showError(
            this.translate.instant('pages.stock.bulkUpload.errorLoadingCenters'),
            this.translate.instant('common.error')
          );
        },
      });
  }

  private setupCenterChangeHandler(): void {
    this.uploadForm.get('centerId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((centerId: number | null) => {
        this.uploadForm.patchValue({ storeId: '' }, { emitEvent: false });
        this.stores = [];
        if (centerId != null) {
          this.uploadForm.get('storeId')?.enable();
          this.loadStores(centerId);
        } else {
          this.uploadForm.get('storeId')?.disable();
        }
      });
  }

  private checkPredefinedStore(): void {
    if (this.data && this.data.storeId) {
      this.isPredefinedStore = true;
      this.uploadForm.patchValue({
        storeId: this.data.storeId
      });
      this.uploadForm.get('storeId')?.disable();

      if (this.data.storeName) {
        this.predefinedStoreName = this.data.storeName;
      } else {
        this.findStoreName();
      }
    }
  }

  private findStoreName(): void {
    if (this.stores.length > 0 && this.data?.storeId) {
      const store = this.stores.find(s => s.id === this.data.storeId);
      if (store) {
        this.predefinedStoreName = store.name;
      }
    }
  }

  getModalTitle(): string {
    if (this.isPredefinedStore && this.predefinedStoreName) {
      return this.translate.instant('pages.stock.bulkUpload.titleWithStore', { storeName: this.predefinedStoreName });
    }
    return this.translate.instant('pages.stock.bulkUpload.title');
  }

  loadCategories(): void {
    this.productCategoryService.getProductCategories().subscribe({
      next: (categories) => {
        this.productCategories = categories;
        this.preselectTiresCategory();
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.snackbarService.showError(
          this.translate.instant('pages.stock.bulkUpload.errorLoadingCategories'),
          this.translate.instant('common.error')
        );
      }
    });
  }

  private preselectTiresCategory(): void {
    const tiresCategory = this.productCategories.find(cat =>
      cat.productCategoryCode.toLowerCase().includes('neumático') ||
      cat.productCategoryCode.toLowerCase().includes('neumatico') ||
      cat.productCategoryCode.toLowerCase().includes('tire') ||
      cat.productCategoryCode.toLowerCase().includes('tyre')
    );

    if (tiresCategory) {
      this.uploadForm.patchValue({
        productCategoryId: tiresCategory.productCategoryId
      });
      this.predefinedCategoryName = tiresCategory.productCategoryCode;
    }
  }

  loadStores(centerId: number | null): void {
    this.isLoadingStores = true;
    this.storeService.getStores(centerId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (stores: StoreDTO[]) => {
        this.stores = stores;
        this.isLoadingStores = false;
        if (this.isPredefinedStore && !this.predefinedStoreName) {
          this.findStoreName();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoadingStores = false;
        console.error('Error loading stores:', error);
        this.snackbarService.showError(
          this.translate.instant('pages.stock.bulkUpload.errorLoadingStores'),
          this.translate.instant('common.error')
        );
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.csvParsing.isSupportedExtension(file.name)) {
      this.fileError = this.translate.instant('pages.stock.bulkUpload.invalidFileFormat');
      this.clearFileState();
      return;
    }

    if (file.size === 0) {
      this.fileError = this.translate.instant('pages.stock.bulkUpload.emptyFile');
      this.clearFileState();
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      this.fileError = this.translate.instant('pages.stock.bulkUpload.fileTooLarge', {
        size: this.maxFileSizeMb
      });
      this.clearFileState();
      return;
    }

    this.selectedFile = file;
    this.fileError = '';
    this.payloadProducts = [];
    this.isMappingReady = false;
    void this.openMappingForFile(file);
  }

  removeFile(): void {
    this.clearFileState();
    const fileInput = document.getElementById('csvFile') as HTMLInputElement | null;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  canProceedToStep2(): boolean {
    if (!this.selectedFile || this.fileError) {
      return false;
    }
    if (!this.isMappingReady || this.payloadProducts.length === 0) {
      return false;
    }
    const formValues = this.uploadForm.getRawValue();
    if (!formValues.productCategoryId) {
      return false;
    }
    if (this.isGroupAdmin && !this.isPredefinedStore && !formValues.centerId) {
      return false;
    }
    if (!this.isPredefinedStore && !formValues.storeId) {
      return false;
    }
    return true;
  }

  proceedToStep2(): void {
    if (!this.canProceedToStep2()) {
      if (!this.isPredefinedCategory) {
        this.uploadForm.get('productCategoryId')?.markAsTouched();
      }
      if (!this.isPredefinedStore) {
        this.uploadForm.get('storeId')?.markAsTouched();
      }
      return;
    }

    this.isProcessing = true;
    this.resetPreviewState();
    this.callPreviewApi();
  }

  callPreviewApi(): void {
    if (this.payloadProducts.length === 0) return;

    const formData = this.uploadForm.getRawValue();
    const categoryId = this.toNumber(formData.productCategoryId);
    const storeId = this.toNumber(formData.storeId);
    if (categoryId == null || storeId == null) {
      this.isProcessing = false;
      this.handleError([this.translate.instant('pages.stock.bulkUpload.errorProcessingFile')]);
      return;
    }

    this.stockService.previewBulkUpload({
      categoryId,
      centerId: this.isGroupAdmin ? this.toNumber(formData.centerId) : null,
      storeId,
      products: [...this.payloadProducts],
      replaceStock: !!formData.replaceStock,
      saveToDatabase: false,
    }).subscribe({
      next: (response: BulkUploadResponse) => {
        this.isProcessing = false;
        if (!response.isSuccess) {
          this.handleError(response.errors);
          return;
        }

        this.previewResponse = response;
        this.parsedProducts = response.products ?? [];
        this.currentStep = 2;
      },
      error: (error: HttpErrorResponse) => {
        this.isProcessing = false;
        console.error('Error in preview API:', error);

        const bulkUploadResponse = this.getBulkUploadResponseFromError(error);
        if (bulkUploadResponse?.errors?.length) {
          this.handleError(bulkUploadResponse.errors);
          return;
        }

        const errors = this.extractErrorMessages(error);
        this.handleError(errors);
      }
    });
  }

  goBackToStep1(): void {
    this.currentStep = 1;
    this.resetPreviewState();
  }

  confirmUpload(): void {
    if (this.parsedProducts.length === 0 || this.payloadProducts.length === 0) return;

    const formData = this.uploadForm.getRawValue();
    const replaceStock = !!formData.replaceStock;
    const lotsToDelete = this.previewResponse?.lotsToDelete ?? 0;

    if (replaceStock && lotsToDelete > 0) {
      void this.openDestructiveConfirmAndSubmit();
      return;
    }

    this.submitConfirmedUpload();
  }

  private async openDestructiveConfirmAndSubmit(): Promise<void> {
    const confirmed = await this.showDestructiveConfirmDialog();
    if (!confirmed) return;
    this.submitConfirmedUpload();
  }

  private showDestructiveConfirmDialog(): Promise<boolean> {
    const data: DestructiveConfirmModalData = {
      title: this.translate.instant('pages.stock.bulkUpload.destructiveConfirm.title'),
      description: this.translate.instant('pages.stock.bulkUpload.destructiveConfirm.description'),
      storeName: this.previewResponse?.storeName
        ?? this.predefinedStoreName
        ?? '',
      lotsToDelete: this.previewResponse?.lotsToDelete ?? 0,
      unitsToDelete: this.previewResponse?.unitsToDelete ?? 0,
      confirmWord: this.translate.instant('pages.stock.bulkUpload.destructiveConfirm.confirmWord'),
    };

    const dialogRef = this.dialog.open<
      DestructiveConfirmModalComponent,
      DestructiveConfirmModalData,
      boolean
    >(DestructiveConfirmModalComponent, {
      data,
      width: '520px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: true,
      panelClass: 'destructive-confirm-dialog',
    });

    return firstValueFrom(dialogRef.afterClosed()).then(result => !!result);
  }

  private submitConfirmedUpload(): void {
    const analyticsRowsResult: ImportRowsResult = {
      amountOfFiles: (this.previewResponse?.processedRows ?? 0) + (this.previewResponse?.failedRows ?? 0),
      validRows: this.previewResponse?.processedRows ?? 0,
      invalidRows: this.previewResponse?.failedRows ?? 0
    };
    this.isProcessing = true;
    const formData = this.uploadForm.getRawValue();
    const categoryId = this.toNumber(formData.productCategoryId);
    const storeId = this.toNumber(formData.storeId);
    if (categoryId == null || storeId == null) {
      this.isProcessing = false;
      return;
    }

    this.analytics.track(AnalyticsEvent.StockImportCsvClick, {
      ...this.analytics.globalProps(),
      file_rows: analyticsRowsResult.amountOfFiles,
      valid_rows: analyticsRowsResult.validRows,
      invalid_rows: analyticsRowsResult.invalidRows,
      warehouse_id: storeId,
    });

    this.stockService.confirmBulkUpload({
      categoryId,
      centerId: this.isGroupAdmin ? this.toNumber(formData.centerId) : null,
      storeId,
      products: [...this.payloadProducts],
      replaceStock: !!formData.replaceStock,
      saveToDatabase: true,
    }, analyticsRowsResult).subscribe({
      next: (response: BulkUploadJobResponse) => {
        this.isProcessing = false;
        if (!response?.jobId) {
          this.handleError([this.translate.instant('pages.stock.bulkUpload.errorSavingProducts')]);
          return;
        }

        // Hand the job off to the global tracker service so the indicator
        // takes over. Close the modal immediately instead of holding it
        // open with a 4s fake delay — the user gets live feedback outside.
        this.jobTracker.trackJob(response.jobId, {
          storeName: this.previewResponse?.storeName ?? this.predefinedStoreName ?? '',
          categoryName: this.previewResponse?.categoryName ?? this.predefinedCategoryName ?? '',
        });
        this.snackbarService.showSuccess(
          this.translate.instant('pages.stock.bulkUpload.backgroundStarted')
        );
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.isProcessing = false;
        console.error('Error in confirm API:', error);
        this.snackbarService.showError(
          this.translate.instant('pages.stock.bulkUpload.errorSavingProducts'),
          this.translate.instant('common.error')
        );
      }
    });
  }

  getSelectedCategoryName(): string {
    return this.previewResponse?.categoryName || '';
  }

  getSelectedStoreName(): string {
    return this.previewResponse?.storeName || '';
  }

  getFailedRowsText(): string {
    if (!this.previewResponse?.failedRowNumbers || this.previewResponse.failedRowNumbers.length === 0) {
      return '';
    }

    return this.previewResponse.failedRowNumbers.join(', ');
  }

  getProductsToCreate(): number {
    if (!this.parsedProducts || this.parsedProducts.length === 0) {
      return 0;
    }
    return this.parsedProducts.filter(product =>
      product.status === 'Will be created' || !product.productExists
    ).length;
  }

  getProductsToUpdate(): number {
    if (!this.parsedProducts || this.parsedProducts.length === 0) {
      return 0;
    }
    return this.parsedProducts.filter(product =>
      product.status === 'Will be updated' || product.productExists
    ).length;
  }

  get hasBlockingValidationErrors(): boolean {
    return this.blockingValidationErrorsCount > 0;
  }

  get blockingValidationErrorsCount(): number {
    if (!this.parsedProducts || this.parsedProducts.length === 0) {
      return 0;
    }
    return this.parsedProducts.filter(product =>
      typeof product.validationError === 'string' && product.validationError.trim().length > 0
    ).length;
  }

  close(): void {
    this.dialogRef.close();
  }

  private extractErrorMessages(error: HttpErrorResponse): string[] {
    const payload = error?.error;
    if (payload && typeof payload === 'object' && 'errors' in payload && Array.isArray(payload.errors)) {
      return payload.errors as string[];
    }
    if (typeof payload === 'string') {
      return [payload];
    }
    if (payload?.message && typeof payload.message === 'string') {
      return [payload.message];
    }
    return [this.translate.instant('pages.stock.bulkUpload.errorProcessingFile')];
  }

  private getBulkUploadResponseFromError(error: HttpErrorResponse): BulkUploadResponse | null {
    const payload = error?.error;
    if (this.isBulkUploadResponse(payload)) {
      return payload;
    }
    return null;
  }

  private isBulkUploadResponse(value: unknown): value is BulkUploadResponse {
    if (!value || typeof value !== 'object') return false;
    return 'errors' in value && 'isSuccess' in value && 'products' in value;
  }

  private handleError(errors: string[]): void {
    this.fileError = '';
    const message = errors.length === 1 ? errors[0] : errors.join('\n\n');
    this.showErrorModal(message);
  }

  private async openMappingForFile(file: File): Promise<void> {
    this.isProcessing = true;
    let parsed: ParsedCsv;
    let schema: ImportSchema;
    try {
      parsed = await this.csvParsing.parseFile(file);
      schema = await firstValueFrom(this.importSchema.getSchema());
    } catch (err) {
      this.isProcessing = false;
      const message = err instanceof CsvParseError
        ? err.message
        : this.translate.instant('pages.stock.bulkUpload.errorProcessingFile');
      this.handleError([message]);
      this.clearFileState();
      return;
    }

    this.isProcessing = false;
    this.openMappingModal(parsed, schema);
  }

  private openMappingModal(parsed: ParsedCsv, schema: ImportSchema): void {
    const dialogRef = this.dialog.open<
      ColumnMappingModalComponent,
      ColumnMappingModalData,
      ColumnMappingResult | null
    >(ColumnMappingModalComponent, {
      data: { parsedCsv: parsed, schema },
      width: '920px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        this.clearFileState();
        return;
      }
      this.onMappingConfirmed(parsed, schema, result);
    });
  }

  private onMappingConfirmed(
    parsed: ParsedCsv,
    schema: ImportSchema,
    result: ColumnMappingResult,
  ): void {
    const build = this.payloadBuilder.build(parsed, schema, result.mapping);
    const blockingErrors = build.errors.filter(e => e.severity === 'error');
    const warnings = build.errors.filter(e => e.severity === 'warning');

    if (build.products.length === 0) {
      this.handleError([this.formatNoValidRowsDetail(blockingErrors)]);
      this.clearFileState();
      return;
    }

    this.payloadProducts = build.products;
    this.isMappingReady = true;

    const skippedRows = this.countDistinctRows(blockingErrors);
    const warnedRows = this.countDistinctRows(warnings);
    if (skippedRows > 0 || warnedRows > 0) {
      this.snackbarService.showInfo(this.formatRowOutcomesMessage(skippedRows, warnedRows));
    }
  }

  private countDistinctRows(errors: ReadonlyArray<RowConversionError>): number {
    const rows = new Set<number>();
    for (const err of errors) rows.add(err.rowNumber);
    return rows.size;
  }

  // Build a multi-line detail string for the "no valid rows" modal so the user can
  // see exactly which field/value failed on which row, instead of a generic message.
  // We sample the first MAX_SHOWN errors and append a "+N more" hint when truncated.
  private formatNoValidRowsDetail(blockingErrors: ReadonlyArray<RowConversionError>): string {
    const header = this.translate.instant('pages.stock.bulkUpload.columnMapping.noValidRows');
    if (blockingErrors.length === 0) return header;

    const MAX_SHOWN = 5;
    const sample = blockingErrors.slice(0, MAX_SHOWN);
    const lines = sample.map(err =>
      this.translate.instant('pages.stock.bulkUpload.columnMapping.noValidRowsRow', {
        row: err.rowNumber,
        field: this.resolveFieldLabel(err.field),
        value: err.rawValue.length > 0 ? err.rawValue : this.translate.instant('pages.stock.bulkUpload.columnMapping.emptyValue'),
        reason: this.resolveReasonLabel(err.reason),
      }),
    );

    let detail = `${header}\n\n${lines.join('\n')}`;
    if (blockingErrors.length > MAX_SHOWN) {
      detail += `\n${this.translate.instant('pages.stock.bulkUpload.columnMapping.noValidRowsMore', {
        count: blockingErrors.length - MAX_SHOWN,
      })}`;
    }
    return detail;
  }

  private formatRowOutcomesMessage(skippedRows: number, warnedRows: number): string {
    if (skippedRows > 0 && warnedRows > 0) {
      return this.translate.instant('pages.stock.bulkUpload.columnMapping.someRowsSkippedAndWarned', {
        skipped: skippedRows,
        warned: warnedRows,
      });
    }
    if (skippedRows > 0) {
      return this.translate.instant('pages.stock.bulkUpload.columnMapping.someRowsSkipped', {
        count: skippedRows,
      });
    }
    return this.translate.instant('pages.stock.bulkUpload.columnMapping.someRowsWarned', {
      count: warnedRows,
    });
  }

  private resolveFieldLabel(property: string): string {
    const key = `pages.stock.bulkUpload.schema.fields.${property}.label`;
    const translated = this.translate.instant(key);
    return translated === key ? property : translated;
  }

  private resolveReasonLabel(reason: string): string {
    const key = `pages.stock.bulkUpload.columnMapping.reasons.${reason}`;
    const translated = this.translate.instant(key);
    return translated === key ? reason : translated;
  }

  private clearFileState(): void {
    this.selectedFile = null;
    this.payloadProducts = [];
    this.isMappingReady = false;
    this.resetPreviewState();
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private showErrorModal(message: string): void {
    this.dialog.open(ConfirmMessageComponent, {
      maxWidth: '600px',
      minWidth: '400px',
      panelClass: 'rounded-dialog',
      data: {
        title: this.translate.instant('common.error'),
        message: message,
        confirmText: this.translate.instant('common.accept'),
        showCancel: false
      }
    });
  }

  showResultsModal(response: BulkUploadResponse): void {
    setTimeout(() => {
      this.isPreparingResults = false;
      const dialogRef = this.dialog.open(BulkUploadResultsModalComponent, {
        data: { response },
        width: '800px',
        maxWidth: '90vw',
        disableClose: false,
        autoFocus: false
      });

      dialogRef.afterClosed().subscribe(() => {
        this.dialogRef.close(true);
      });
    }, 2000);
  }

  private setupPredefinedCategory(): void {
    this.uploadForm.get('productCategoryId')?.disable();
  }


  private resetPreviewState(): void {
    this.previewResponse = null;
    this.parsedProducts = [];
  }



  downloadExampleCSV(): void {
    const headers = ['Codigo del articulo', 'EAN', 'Stock', 'Precio', 'DOT'];

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'example.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
